import {useState} from 'react';
import Icon from '../../../components/AppIcon';
import * as LucideIcons from "lucide-react";


const ProblemSection = () => {
    const [activeTab, setActiveTab] = useState('manual');

    const problems = [
        {
            icon: 'AlertTriangle',
            title: 'Revenue Leakage',
            description: 'Up to 60% of collected revenue never reaches assembly accounts due to cash handling',
            stat: '₵2.4M Lost Annually'
        },
        {
            icon: 'Clock',
            title: 'Inefficient Collection',
            description: 'Manual processes take 3x longer and require extensive paperwork',
            stat: '72 Hours Average'
        },
        {
            icon: 'Users',
            title: 'Citizen Frustration',
            description: 'Long queues and lack of receipts create poor service experience',
            stat: '78% Dissatisfaction'
        },
        {
            icon: 'FileX',
            title: 'Poor Accountability',
            description: 'No real-time tracking or transparent reporting mechanisms',
            stat: '0% Transparency'
        }
    ];

    const solutions = [
        {
            icon: 'Smartphone',
            title: 'Digital Payments',
            description: '100% traceable transactions with instant mobile money integration',
            stat: '100% Accountability'
        },
        {
            icon: 'Zap',
            title: 'Real-time Processing',
            description: 'Instant payment confirmation and automatic receipt generation',
            stat: '5 Seconds Average'
        },
        {
            icon: 'Heart',
            title: 'Citizen Satisfaction',
            description: 'Convenient online payments and transparent service delivery',
            stat: '94% Satisfaction'
        },
        {
            icon: 'BarChart3',
            title: 'Complete Visibility',
            description: 'Real-time dashboards and comprehensive audit trails',
            stat: '100% Transparent'
        }
    ];

    return (
        <section className="py-20 bg-muted/30">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                        The Challenge Facing Ghana's Local Assemblies
                    </h2>
                    <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                        Manual revenue collection systems are failing citizens and assemblies alike.
                        See how digital transformation eliminates these critical issues.
                    </p>
                </div>

                {/* Tab Navigation */}
                <div className="flex justify-center mb-12">
                    <div className="bg-white rounded-lg p-1 shadow-sm border border-border">
                        <button
                            onClick={() => setActiveTab('manual')}
                            className={`px-6 py-3 rounded-md font-medium transition-all duration-200 ${
                                activeTab === 'manual' ? 'bg-error text-error-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            Current Manual System
                        </button>
                        <button
                            onClick={() => setActiveTab('digital')}
                            className={`px-6 py-3 rounded-md font-medium transition-all duration-200 ${
                                activeTab === 'digital' ? 'bg-success text-success-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            Civicrate Digital Solution
                        </button>
                    </div>
                </div>

                {/* Split Screen Comparison */}
                <div className="grid lg:grid-cols-2 gap-12">
                    {/* Manual System Problems */}
                    <div className={`space-y-6 ${activeTab === 'manual' ? 'block' : 'hidden lg:block opacity-50'}`}>
                        <div className="bg-white rounded-xl p-8 border-l-4 border-error shadow-lg">
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-12 h-12 bg-error/10 rounded-lg flex items-center justify-center">
                                    <Icon name="AlertCircle" size={24} color="var(--color-error)"/>
                                </div>
                                <h3 className="text-2xl font-bold text-foreground">Manual Collection Issues</h3>
                            </div>

                            <div className="space-y-6">
                                {problems?.map((problem, index) => (
                                    <div key={index} className="flex items-start space-x-4 p-4 bg-error/5 rounded-lg">
                                        <div
                                            className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <Icon name={problem?.icon as keyof typeof LucideIcons} size={20}
                                                  color="var(--color-error)"/>
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="font-semibold text-foreground mb-1">{problem?.title}</h4>
                                            <p className="text-sm text-muted-foreground mb-2">{problem?.description}</p>
                                            <span className="text-sm font-bold text-error">{problem?.stat}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Visual Representation */}
                            <div className="mt-8 p-6 bg-error/5 rounded-lg">
                                <div className="flex items-center justify-between mb-4">
                                    <span className="text-sm font-medium text-muted-foreground">Revenue Collection Efficiency</span>
                                    <span className="text-sm font-bold text-error">40%</span>
                                </div>
                                <div className="w-full bg-error/20 rounded-full h-3">
                                    <div className="bg-error h-3 rounded-full" style={{width: '40%'}}/>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Digital Solution Benefits */}
                    <div className={`space-y-6 ${activeTab === 'digital' ? 'block' : 'hidden lg:block opacity-50'}`}>
                        <div className="bg-white rounded-xl p-8 border-l-4 border-success shadow-lg">
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                                    <Icon name="CheckCircle" size={24} color="var(--color-success)"/>
                                </div>
                                <h3 className="text-2xl font-bold text-foreground">Civicrate Digital Benefits</h3>
                            </div>

                            <div className="space-y-6">
                                {solutions?.map((solution, index) => (
                                    <div key={index} className="flex items-start space-x-4 p-4 bg-success/5 rounded-lg">
                                        <div
                                            className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <Icon name={solution?.icon as keyof typeof LucideIcons} size={20}
                                                  color="var(--color-success)"/>
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="font-semibold text-foreground mb-1">{solution?.title}</h4>
                                            <p className="text-sm text-muted-foreground mb-2">{solution?.description}</p>
                                            <span className="text-sm font-bold text-success">{solution?.stat}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Visual Representation */}
                            <div className="mt-8 p-6 bg-success/5 rounded-lg">
                                <div className="flex items-center justify-between mb-4">
                                    <span className="text-sm font-medium text-muted-foreground">Revenue Collection Efficiency</span>
                                    <span className="text-sm font-bold text-success">98%</span>
                                </div>
                                <div className="w-full bg-success/20 rounded-full h-3">
                                    <div className="bg-success h-3 rounded-full" style={{width: '98%'}}/>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Statistics Showcase */}
                <div className="mt-16 bg-white rounded-xl p-8 shadow-lg">
                    <h3 className="text-2xl font-bold text-center text-foreground mb-8">
                        Pilot MMDA Results After 6 Months
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div
                                className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Icon name="TrendingUp" size={32} color="var(--color-primary)"/>
                            </div>
                            <div className="text-3xl font-bold text-primary mb-2">+340%</div>
                            <p className="text-muted-foreground">Revenue Increase</p>
                        </div>

                        <div className="text-center">
                            <div
                                className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Icon name="Clock" size={32} color="var(--color-success)"/>
                            </div>
                            <div className="text-3xl font-bold text-success mb-2">85%</div>
                            <p className="text-muted-foreground">Time Reduction</p>
                        </div>

                        <div className="text-center">
                            <div
                                className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Icon name="Users" size={32} color="var(--color-accent)"/>
                            </div>
                            <div className="text-3xl font-bold text-accent mb-2">94%</div>
                            <p className="text-muted-foreground">Citizen Satisfaction</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ProblemSection;